/**
 * i18next bootstrap. Imported for its side effect exactly once, from
 * src/index.jsx.
 *
 * Why a module-scope side effect rather than a React provider: Login,
 * LandingPage and ETLProjectPage are rendered OUTSIDE AppProviders (see
 * src/index.jsx), so anything provider-shaped would miss them. `initReactI18next`
 * registers this instance as the default one, which is what lets `useTranslation()`
 * work anywhere without an <I18nextProvider> wrapper.
 *
 * There is deliberately no LanguageContext in this codebase. useTranslation()
 * already subscribes its component to i18next's `languageChanged` event and
 * re-renders it; i18n.resolvedLanguage is readable from non-React modules too
 * (see intlLocale.js). A context would be a second source of truth for a value
 * the library already owns, and would have to be kept in sync with it.
 *
 * CONSEQUENCE worth knowing: a component that renders formatted numbers or
 * dates but never calls useTranslation() will not re-render on a language
 * switch, and will show stale separators until something else re-renders it.
 * The rule is therefore: if a component displays formatted numbers or dates,
 * it calls useTranslation().
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import resources from './resources';
import {
  DEFAULT_LANGUAGE,
  FALLBACK_LANGUAGE,
  SUPPORTED_LANGUAGES,
} from './config';
import { getStoredLanguage } from '../services/languageStorage';

i18n.use(initReactI18next).init({
  resources,
  // getStoredLanguage() already validates against SUPPORTED_LANGUAGES and
  // returns null for anything unknown, so this can never resolve to a language
  // we have no resources for.
  lng: getStoredLanguage() || DEFAULT_LANGUAGE,
  fallbackLng: FALLBACK_LANGUAGE,
  supportedLngs: SUPPORTED_LANGUAGES,
  // English is the source language, so it defines the full namespace set.
  ns: Object.keys(resources[FALLBACK_LANGUAGE]),
  defaultNS: 'common',
  interpolation: {
    // React escapes interpolated values already; escaping twice mangles
    // anything containing & < > in a translated string.
    escapeValue: false,
  },
  returnNull: false,
  react: {
    // Resources are bundled, so init is synchronous and there is nothing to
    // suspend on.
    useSuspense: false,
  },
  debug: import.meta.env.DEV,
});

export default i18n;
